import { Box, Search, Tabs } from "@procore/core-react";
import { useTranslation } from "react-i18next";

import { BaseWindowHeader } from "components/window/BaseWindowHeader";
import { HEADER_BORDER } from "components/window/constants";
import { CORE_PROPERTIES_TAB_IDS, usePropertiesTabs } from "contexts/propertiesTabs";

interface PropertiesWindowHeaderProps {
    objectName: string;
    activeTab: string;
    searchTerm: string;
    onTabChange: (tab: string) => void;
    onSearchChange: (value: string) => void;
    onClose?: () => void;
}

export function PropertiesWindowHeader({
    objectName,
    activeTab,
    searchTerm,
    onTabChange,
    onSearchChange,
    onClose,
}: PropertiesWindowHeaderProps) {
    const { t } = useTranslation();
    const customTabs = usePropertiesTabs();

    const allTabs = Object.values(customTabs.tabEntries).map((tabEntry) => ({
        id: tabEntry.config.id,
        label:
            tabEntry.config.id === CORE_PROPERTIES_TAB_IDS.PROPERTIES
                ? t("properties") // Translate core properties tab
                : tabEntry.config.label, // Custom tabs already translated when added
    }));

    return (
        <BaseWindowHeader title={t("properties")} subtitle={objectName || t("noObjectSelected")} onClose={onClose}>
            <Box style={{ paddingLeft: "16px", paddingRight: "16px", borderBottom: HEADER_BORDER }}>
                <Tabs data-testid="properties-tabs">
                    {allTabs.map((tab) => (
                        <Tabs.Tab
                            key={tab.id}
                            selected={activeTab === tab.id}
                            onClick={() => onTabChange(tab.id)}
                            data-testid={`properties-tab-${tab.id}`}
                        >
                            {tab.label}
                        </Tabs.Tab>
                    ))}
                </Tabs>
            </Box>
            {activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES && (
                <Box
                    style={{
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        paddingBottom: "8px",
                        paddingTop: "8px",
                        borderBottom: HEADER_BORDER,
                    }}
                >
                    <Search
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={t("searchProperties")}
                        autoComplete="off"
                    />
                </Box>
            )}
        </BaseWindowHeader>
    );
}
